/**
 * B26 / WS-4 G5 — **the signed-in operator's own profile.** `GET /v1/auth/me` on `cp-auth-api`.
 *
 * # ⛔⛔ ITS OWN MODULE, AND THAT IS A GATE-INTEGRITY DECISION DISGUISED AS FILE LAYOUT
 *
 * ⛔ **Do not fold this into another api module.** `permission-gate-parity.test.ts` is satisfied by
 * **ANY ONE** of a module's declared permissions, so a module declaring two slugs lets either screen
 * discharge its gate obligation on the *other* screen's permission — and the gate goes green over the
 * exact confusion it exists to catch. `envConfig.api.ts`'s header records the shipped defect that rule
 * comes from.
 *
 * # ⛔ THIS ROUTE HAS NO `@RequirePermission`, AND THE ABSENCE IS STATED SO NOBODY ADDS ONE
 *
 * `GET /v1/auth/me` is **self-service**: it is guarded by `CpJwtAuthGuard` only, and it returns facts
 * about the caller's *own* operator row. ⇒ this module declares **no permission constant**, deliberately.
 *
 * ⚠️ Adding one would be actively wrong in this codebase, not merely redundant: under **R7-DEC-5 a
 * permission HIDES**, so gating the user menu behind a slug would make the operator's own identity — and
 * the sign-out control that lives beside it — disappear for any principal lacking that slug. The
 * zero-access principal is precisely the one who most needs a way out of the session.
 *
 * # ⚠️ IT IS ALSO PROVISIONING-SVC'S TOKEN-VALIDATION PROBE
 *
 * `validate-operator-token.handler.ts` treats a **403** from this route as *"check `CP_JWT_SECRET`"*. So
 * the route's status codes and its first-party key **order** are a contract with a second consumer.
 * ⛔ Never widen this client to send a body, a query or a header that could change the response shape.
 */
import { api } from '../platform/http/client';

export const PROFILE_PATH = '/v1/auth/me';

/**
 * The signed-in operator, as `/v1/auth/me` returns it.
 *
 * ⚠️ **Hand-declared here, hand-declared in `auth.controller.ts` — there is no shared contract.** The
 * handler builds a plain object literal with no zod schema and no OpenAPI binding, so this type is a
 * faithful COPY rather than an import. Keep it in step by hand; nothing compares them.
 *
 * ⛔ **`role` is genuinely OPTIONAL and that is a disclosure boundary, not sloppiness.** The handler
 * spreads it in only when `user.scp === undefined` (a first-party token); a delegated token gets the key
 * **absent** from the JSON. ⇒ `role?: string`, and a consumer must not assume it.
 */
export interface OperatorProfile {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  /** ⛔ ABSENT for a delegated caller — see the interface docblock. */
  readonly role?: string;
  readonly status: string;
  readonly createdAt: string;
  /**
   * The operator's avatar URL, or `null`.
   *
   * ⭐ **Already validated at the producer's write boundary** (`cp-auth-api`'s `sanitizeAvatarUrl`):
   * https, an allow-listed Google host, ≤512 chars, no embedded credentials. So a consumer does not
   * re-validate the scheme or the host.
   *
   * ⛔ **It still needs render-time hardening** — `referrerPolicy="no-referrer"` on the `<img>` — because
   * the write boundary and the sink answer different questions: the column being trustworthy does not
   * stop the browser leaking the console's URL to Google on every avatar fetch.
   *
   * ⚠️ `null` is a permanent, legitimate state (no photo, a github login, or a claim that failed
   * validation), **not** a loading artifact — render the monogram, never a broken `<img>`.
   */
  readonly avatarUrl: string | null;
}

/**
 * Read the signed-in operator's profile.
 *
 * ⚠️ Returns `null` when there is no operator row for the token's subject — the handler's own contract
 * (`if (!operator) return null`), which is reachable for a validly-signed token whose operator was
 * deleted mid-session. Callers must treat `null` as "render the fallback", not as an error.
 */
export function getOperatorProfile(): Promise<OperatorProfile | null> {
  return api.get<OperatorProfile | null>(PROFILE_PATH);
}
